if (typeof BackorderNotice !== "function") {
  class BackorderNotice extends HTMLElement {
    constructor() {
      super();
      this._onVariantChange = this._onVariantChange.bind(this);
      this._onQuantityChange = this._onQuantityChange.bind(this);
    }

    connectedCallback() {
      try {
        this.variants = JSON.parse(this.dataset.variants || "{}");
      } catch (_) {
        this.variants = {};
      }
      this.currentVariantId = this.dataset.variantId || null;

      const formId = this.getAttribute("form");
      this.form = formId ? document.forms[formId] : this.closest("form");
      if (this.form) {
        this.form.addEventListener("variant:change", this._onVariantChange);
      }

      this.quantityInputs = this._findQuantityInputs(formId);
      this.quantityInputs.forEach((input) => {
        input.addEventListener("input", this._onQuantityChange);
        input.addEventListener("change", this._onQuantityChange);
      });

      this._observer = new MutationObserver(this._onQuantityChange);
      this.quantityInputs.forEach((input) => {
        this._observer.observe(input, { attributes: true, attributeFilter: ["value", "data-value"] });
      });

      this._applyQuantityLimit();
      this._update();
    }

    disconnectedCallback() {
      this.form?.removeEventListener("variant:change", this._onVariantChange);
      this.quantityInputs?.forEach((input) => {
        input.removeEventListener("input", this._onQuantityChange);
        input.removeEventListener("change", this._onQuantityChange);
      });
      this._observer?.disconnect();
    }

    _onVariantChange(event) {
      const variant = event.detail?.variant;
      this.currentVariantId = variant ? String(variant.id) : null;
      this._applyQuantityLimit();
      this._update();
    }

    _onQuantityChange() {
      this._update();
    }

    _currentVariantInfo() {
      return this.currentVariantId ? this.variants[this.currentVariantId] : null;
    }

    _findQuantityInputs(formId) {
      const inputs = new Set();
      if (formId) {
        document
          .querySelectorAll(`input[name="quantity"][form="${formId}"]`)
          .forEach((input) => inputs.add(input));
      }
      if (this.form) {
        this.form.querySelectorAll('input[name="quantity"]').forEach((input) => inputs.add(input));
      }
      return Array.from(inputs);
    }

    _getQuantity() {
      const input = this.quantityInputs?.[0];
      const raw = input ? parseInt(input.value, 10) : 1;
      return Number.isFinite(raw) && raw > 0 ? raw : 1;
    }

    _applyQuantityLimit() {
      if (!this.quantityInputs?.length) return;
      const info = this._currentVariantInfo();

      let maxQuantity = null;
      if (info && info.managed) {
        if (info.policy === "deny" && info.quantity > 0) {
          maxQuantity = info.quantity;
        } else if (info.policy === "continue" && info.quantity <= 0) {
          maxQuantity = Math.max(0, 10 + info.quantity);
        }
      }

      this.quantityInputs.forEach((input) => {
        if (maxQuantity !== null) {
          input.max = String(maxQuantity);
          const current = parseInt(input.value, 10);
          if (Number.isFinite(current) && current > maxQuantity) {
            input.value = String(Math.max(1, maxQuantity));
            input.dispatchEvent(new Event("change", { bubbles: true }));
          }
        } else {
          input.removeAttribute("max");
        }
      });
    }

    _shouldShow() {
      const info = this._currentVariantInfo();
      if (!info || !info.managed || info.policy !== "continue") return false;

      const quantity = this._getQuantity();
      return info.quantity <= 0 || quantity > info.quantity;
    }

    _update() {
      this.classList.toggle("hidden", !this._shouldShow());
    }
  }

  if (typeof customElements.get("backorder-notice") === "undefined") {
    customElements.define("backorder-notice", BackorderNotice);
  }
}
